package music.jammers.client.shared.component.track.presentation.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardColors
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import limasoul.client.shared.generated.resources.Res
import limasoul.client.shared.generated.resources.event_detail_screen_track_artist_and_track
import limasoul.client.shared.generated.resources.ic_my_track_star
import limasoul.client.shared.generated.resources.ic_not_ready
import limasoul.client.shared.generated.resources.ic_ready
import music.jammers.client.shared.component.cardTextDark
import music.jammers.client.shared.component.cardTextLight
import music.jammers.client.shared.component.toColor
import music.jammers.client.shared.component.track.presentation.model.TrackCardModel
import music.jammers.shared.event.domain.model.EventMember
import music.jammers.shared.event.track.domain.model.BackgroundType
import music.jammers.shared.event.track.domain.model.EventTrackBackgroundInfo
import music.jammers.shared.track.domain.model.Track
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import kotlin.math.sin

private const val MAX_TRACK_NAME_WIDTH = 260

@Composable
fun EventTrackCardColors(backgroundInfo: EventTrackBackgroundInfo): CardColors {
    val containerColor = backgroundInfo.color.toColor()
    val textColor = when (backgroundInfo.type) {
        BackgroundType.LIGHT -> cardTextDark
        BackgroundType.DARK -> cardTextLight
    }

    return CardDefaults.cardColors().copy(
        containerColor = containerColor,
        contentColor = textColor,
        disabledContainerColor = containerColor,
        disabledContentColor = textColor,
    )
}

private const val CORNER_RADIUS = 30

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun TrackCard(
    modifier: Modifier = Modifier,
    trackCardModel: TrackCardModel,
    onClick: (Long) -> Unit,
    onMemberClicked: (Track, EventMember) -> Unit,
) {
    Box(
        modifier = modifier.padding(
            top = 0.dp,
            start = 10.dp,
            end = 0.dp,
            bottom = 0.dp,
        ).fillMaxWidth(),
    ) {
        val hasAvailableMembers = trackCardModel.availableMembers.isNotEmpty()

        Card(
            modifier = Modifier
                .padding(
                    top = 0.dp,
                    bottom = 0.dp,
                    start = 0.dp,
                    end = 10.dp,
                )
                .clickable {
                    onClick(trackCardModel.eventTrack.id)
                },
            shape = RoundedCornerShape(CORNER_RADIUS.dp),
            colors = EventTrackCardColors(trackCardModel.eventTrack.backgroundInfo),
        ) {
            Row(
                modifier = Modifier.padding(
                    top = 16.dp,
                    start = 26.dp,
                    end = 0.dp,
                    bottom = 16.dp,
                ),
            ) {
                Column {
                    Text(
                        text = stringResource(
                            Res.string.event_detail_screen_track_artist_and_track,
                            trackCardModel.eventTrack.track.artist.name,
                            trackCardModel.eventTrack.track.name,
                        ),
                        maxLines = 1,
                        style = MaterialTheme.typography.headlineMedium,
                        fontSize = 20.sp,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.sizeIn(
                            minWidth = MAX_TRACK_NAME_WIDTH.dp,
                            minHeight = 0.dp,
                            maxWidth = MAX_TRACK_NAME_WIDTH.dp,
                            maxHeight = Dp.Unspecified,
                        )
                    )

                    if (hasAvailableMembers) {
                        Spacer(modifier = Modifier.size(14.dp))
                    }

                    FlowRow {
                        trackCardModel.availableMembers.forEach { item ->
                            TrackMember(
                                eventMember = item,
                                backgroundType = trackCardModel.eventTrack.backgroundInfo.type,
                                onClicked = { eventMember ->
                                    onMemberClicked(trackCardModel.eventTrack.track, eventMember)
                                }
                            )
                            Spacer(modifier = Modifier.size(6.dp))
                        }
                    }
                }

                Spacer(modifier = Modifier.weight(1.0f).defaultMinSize(10.dp))

                val readyStatus = if (trackCardModel.eventTrack.isReady) {
                    Res.drawable.ic_ready
                } else {
                    Res.drawable.ic_not_ready
                }

                Icon(
                    painter = painterResource(readyStatus),
                    contentDescription = "ReadyIcon",
                    modifier = Modifier.align(Alignment.CenterVertically)
                )

                Spacer(modifier = Modifier.width(40.dp))
            }
        }

        if (trackCardModel.isMyTrack) {
            val padding = CORNER_RADIUS - sin(x = 45f) * CORNER_RADIUS

            Icon(
                painter = painterResource(Res.drawable.ic_my_track_star),
                contentDescription = "TrackStar",
                modifier = Modifier.align(Alignment.TopEnd)
                    .padding(
                        top = padding.dp,
                        end = padding.dp,
                        start = 0.dp,
                        bottom = 0.dp,
                    ),
            )
        }
    }
}